import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { NewAddress } from "@/types/settings";
import { ValidatedFormField } from "@/admin/settings/customer/dealer/components/ValidatedFormField";
import {
  validateCompanyName,
  validateContactPerson,
  validateAddress,
  validatePostalCode,
  validateCity,
} from "@/utils/formValidation";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { settingsLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { DeliveryAddress } from "@/services/settings/settingsService";

interface AddressFormProps {
  onAddressAdded: (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => Promise<void> | void;
  onAddressUpdated?: (address: {
    street: string;
    postalCode: string;
    city: string;
    country?: string;
    isDefault?: boolean;
  }) => Promise<void> | void;
  editAddressId?: string | null;
  addresses?: DeliveryAddress[];
  onCancelEdit?: () => void;
}

export const AddressForm = ({
  onAddressAdded,
  onAddressUpdated,
  editAddressId,
  addresses = [],
  onCancelEdit,
}: AddressFormProps) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === "dark";
  const labels = settingsLabels(getLanguage());
  const [showForm, setShowForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const editingAddress = editAddressId
    ? addresses.find((a) => a.id === editAddressId)
    : null;

  const [newAddress, setNewAddress] = useState<NewAddress>({
    street: editingAddress?.street || "",
    postal_code: editingAddress?.postalCode || "",
    city: editingAddress?.city || "",
    country: editingAddress?.country || labels.delivery.sweden,
  });

  // Validation state for form fields
  const [fieldValidations, setFieldValidations] = useState({
    street: { isValid: false, errors: [] },
    postal_code: { isValid: false, errors: [] },
    city: { isValid: false, errors: [] },
  });

  const isFormValid = Object.values(fieldValidations).every(
    (field) => field.isValid
  );

  // Update form when editing address
  useEffect(() => {
    if (editAddressId && editingAddress) {
      setNewAddress({
        street: editingAddress.street,
        postal_code: editingAddress.postalCode,
        city: editingAddress.city,
        country: editingAddress.country,
      });
      setShowForm(true);
      // Validate existing values
      setFieldValidations({
        street: validateAddress(editingAddress.street),
        postal_code: validatePostalCode(editingAddress.postalCode),
        city: validateCity(editingAddress.city),
      });
    } else if (!editAddressId) {
      setNewAddress({
        street: "",
        postal_code: "",
        city: "",
        country: labels.delivery.sweden,
      });
      setFieldValidations({
        street: { isValid: false, errors: [] },
        postal_code: { isValid: false, errors: [] },
        city: { isValid: false, errors: [] },
      });
      setShowForm(false); // Close edit form when edit is cancelled or saved successfully
    }
  }, [editAddressId, editingAddress, labels.delivery.sweden]);

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast.error(labels.delivery.fillAllFields);
      return;
    }

    setIsSaving(true);
    try {
      const addressData = {
        street: newAddress.street,
        postalCode: newAddress.postal_code,
        city: newAddress.city,
        country: newAddress.country,
      };

      if (editAddressId && onAddressUpdated) {
        // Updating existing address - parent will set editAddressId to null on success to close form
        await onAddressUpdated(addressData);
      } else {
        // Adding new address - if this is the first address, set it as default
        const isFirstAddress = addresses.length === 0;
        await onAddressAdded({
          ...addressData,
          isDefault: isFirstAddress,
        });
        setNewAddress({
          street: "",
          postal_code: "",
          city: "",
          country: labels.delivery.sweden,
        });
        setShowForm(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (
    field: string,
    value: string,
    isValid: boolean,
    errors: string[] = []
  ) => {
    setNewAddress((prev) => ({ ...prev, [field]: value }));
    setFieldValidations((prev) => ({
      ...prev,
      [field]: { isValid, errors },
    }));
  };

  const cardBg = isDark
    ? "bg-black/20 border-white/10"
    : "bg-white/90 border-gray-200";

  return (
    <div className="space-y-4">
      {!showForm && !editAddressId ? (
        <Button onClick={() => setShowForm(true)} className="w-full">
          {labels.delivery.addAddress}
        </Button>
      ) : (
        <div className={`space-y-4 p-4 rounded-lg ${cardBg}`}>
          <h3 className="text-lg font-semibold">
            {editAddressId
              ? labels.delivery.editAddress || "Redigera adress"
              : labels.delivery.addAddress}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ValidatedFormField
              id="street"
              label={labels.delivery.street}
              placeholder={labels.delivery.street}
              required
              value={newAddress.street}
              onChange={(value) => {
                const validation = validateAddress(value);
                handleFieldChange(
                  "street",
                  value,
                  validation.isValid,
                  validation.errors
                );
              }}
              validator={validateAddress}
            />
            <ValidatedFormField
              id="postal_code"
              label={labels.delivery.postalCode}
              placeholder="12345"
              required
              value={newAddress.postal_code}
              onChange={(value) => {
                const validation = validatePostalCode(value);
                handleFieldChange(
                  "postal_code",
                  value,
                  validation.isValid,
                  validation.errors
                );
              }}
              validator={validatePostalCode}
            />
            <ValidatedFormField
              id="city"
              label={labels.delivery.city}
              placeholder={labels.delivery.cityPlaceholder || "Stockholm"}
              required
              value={newAddress.city}
              onChange={(value) => {
                const validation = validateCity(value);
                handleFieldChange(
                  "city",
                  value,
                  validation.isValid,
                  validation.errors
                );
              }}
              validator={validateCity}
            />
            <div className="space-y-2">
              <label htmlFor="country" className="text-sm font-medium">
                {labels.delivery.country}
              </label>
              <Select
                value={newAddress.country}
                onValueChange={(value) =>
                  setNewAddress({ ...newAddress, country: value })
                }
              >
                <SelectTrigger
                  className={
                    isDark
                      ? "bg-transparent border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-800"
                  }
                >
                  <SelectValue placeholder={labels.delivery.selectCountry} />
                </SelectTrigger>
                <SelectContent
                  className={
                    isDark
                      ? "bg-black/90 border-white/20"
                      : "bg-white border-gray-200"
                  }
                >
                  <SelectItem
                    value={labels.delivery.sweden}
                    className={
                      isDark
                        ? "text-white hover:bg-white/10"
                        : "text-gray-800 hover:bg-gray-100"
                    }
                  >
                    {labels.delivery.sweden}
                  </SelectItem>
                  <SelectItem
                    value={labels.delivery.norway}
                    className={
                      isDark
                        ? "text-white hover:bg-white/10"
                        : "text-gray-800 hover:bg-gray-100"
                    }
                  >
                    {labels.delivery.norway}
                  </SelectItem>
                  <SelectItem
                    value={labels.delivery.denmark}
                    className={
                      isDark
                        ? "text-white hover:bg-white/10"
                        : "text-gray-800 hover:bg-gray-100"
                    }
                  >
                    {labels.delivery.denmark}
                  </SelectItem>
                  <SelectItem
                    value={labels.delivery.finland}
                    className={
                      isDark
                        ? "text-white hover:bg-white/10"
                        : "text-gray-800 hover:bg-gray-100"
                    }
                  >
                    {labels.delivery.finland}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSubmit}
              className="flex-1"
              disabled={!isFormValid || isSaving}
            >
              {isSaving
                ? labels.delivery.saving || "Sparar..."
                : editAddressId
                ? labels.delivery.saveChanges || "Spara ändringar"
                : labels.delivery.addAddressButton}
            </Button>
            <Button
              onClick={() => {
                setShowForm(false);
                if (onCancelEdit) onCancelEdit();
              }}
              variant="outline"
              className={`flex-1 ${
                isDark
                  ? "border-white/20 text-white hover:bg-white/10"
                  : "border-gray-300 text-gray-800 hover:bg-gray-100"
              }`}
            >
              {labels.delivery.cancel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
